import { Controller } from "react-hook-form";
import { useForm } from "react-hook-form";

import { randomCrector } from "@/features/Board/BoardCrector/randomCrector";
import { useCallback, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { type CommentFormSchema, dynamicSchema } from "../schema";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import useThrottling from "@/hooks/useThrottling";
import { cn } from "@/lib/utils";
import { ReplyIcon } from "lucide-react";
import ErrorBubble from "@/components/error/ErrorBubble";
import SupabasePool from "@/lib/supabaseClient";
import { useCommentForm } from "@/hooks/useCommentForm";
import { getAdminByEmail } from "@/services/getAdminByEmail";
import { useAuthentication } from "@/hooks/useAuth";

type CommentFormValues = z.infer<ReturnType<typeof dynamicSchema>>;

export default function BoardCommentForm({
  parent_id,
}: {
  parent_id?: null | number;
}) {
  const authentication = useAuthentication();
  const { deleteId } = useCommentForm();

  const { throttle } = useThrottling();
  const queryClient = useQueryClient();

  const pool = SupabasePool.getInstance();

  const defaultValues = useCallback((parent_id?: number | null) => {
    return {
      guest: "",
      userIcon: "",
      comment: "",
      ...(parent_id && { parent_id }),
    };
  }, []);

  const form = useForm<CommentFormSchema>({
    defaultValues: defaultValues(parent_id),
    resolver: zodResolver(dynamicSchema(!!parent_id, !!authentication)),
  });

  const { mutate } = useMutation({
    mutationFn: async (payload: CommentFormValues) => {
      const { data: session } = await pool.auth.getSession();

      // 로그인 유저 (admin/super)
      if (session.session) {
        const adminMail = session.session.user.user_metadata.email;

        // 관리자 정보 겟
        const response = await getAdminByEmail({ pool, email: adminMail });

        if (!response.ok || !response.data?.id) {
          throw new Error(
            `관리자 정보 조회 실패: ${response.error?.message ?? "id 없음"}`
          );
        }

        const { data, error } = await pool.rpc("insert_admin_with_board", {
          author_id: response.data.id,
          comment: payload.comment,
          parent_id: payload.parent_id ?? null,
          author_type: response.data.role,
        });

        if (error) {
          throw new Error(`관리자 댓글 등록 실패: ${error.message}`);
        }
        if (!data) {
          throw new Error("관리자 댓글 등록 실패: 반환값 없음");
        }

        return data;
      }

      // 게스트
      const { data, error } = await pool.rpc("insert_guest_with_board", {
        nickname: payload.guest,
        guest_icon: payload.userIcon,
        comment: payload.comment,
        parent_id: payload.parent_id ?? null,
      });

      if (error) {
        throw new Error(`게스트 댓글 등록 실패: ${error.message}`);
      }

      return data;
    },
    onError: (err: unknown) => {
      // react-query onError 훅으로 에러 메시지 출력
      const message =
        err instanceof Error ? err.message : "알 수 없는 에러 발생";
      toast.error(message);
    },
    onSuccess: () => {
      toast.success("등록 되었습니다.");
      form.reset(defaultValues(parent_id));
      if (parent_id) deleteId();
      queryClient.invalidateQueries({ queryKey: ["GUESTBOARD"] });
    },
  });

  const onSubmitHandlr = (data: CommentFormValues) => {
    throttle(async () => mutate(data), 1500);
  };

  useEffect(() => {
    form.reset({
      ...defaultValues(),
      userIcon: randomCrector(authentication),
      ...(parent_id && { parent_id }),
    });
  }, [parent_id, authentication]);

  // Error Message
  const errors = Object.values(form.formState.errors).map((e) => e.message);

  return (
    <>
      <form
        className={cn(
          "w-full flex flex-col items-start relative gap-2",
          !!parent_id && "animate-wiggle"
        )}
        method="POST"
        onSubmit={form.handleSubmit(onSubmitHandlr)}
      >
        {errors.length > 0 && <ErrorBubble>{errors[0]}</ErrorBubble>}
        <div className="flex flex-col md:flex-row md:lex-wrap gap-3 w-full mt-5">
          {!authentication && (
            <>
              <Controller
                name="guest"
                control={form.control}
                render={({ field }) => (
                  <div
                    className={cn(
                      "grid  border border-zinc-50/20 w-full p-2  items-center rounded-lg focus-within:border-violet-300",
                      form.formState.errors.guest && "border-red-400"
                    )}
                    style={{
                      backdropFilter: "blur(4px)",
                    }}
                  >
                    <input
                      className={cn("focus:outline-0 px-2 py-1")}
                      {...field}
                      type={"guest"}
                      placeholder={"닉네임을 2자리 이상 적어주세요!"}
                      autoComplete="off"
                    />
                  </div>
                )}
              />
            </>
          )}
        </div>

        <div
          className={cn(
            "grid grid-cols-[1fr_auto] border border-zinc-50/20  w-full p-2 items-center rounded-lg",
            "focus-within:border-violet-300",
            form.formState.errors.comment && "border-red-400"
          )}
          style={{
            backdropFilter: "blur(4px)",
          }}
        >
          <Controller
            name="comment"
            control={form.control}
            render={({ field }) => (
              <textarea
                className="w-full focus:outline-0 resize-none pl-2 "
                {...field}
                placeholder={"남기실 댓글 내용을 입력해주세요!"}
                autoComplete="off"
              />
            )}
          />{" "}
          <button className="ml-auto text-xs size-13 bg-zinc-50 rounded-xl flex justify-center items-center">
            <ReplyIcon className="text-zinc-900 size-5 rotate-180" />
          </button>
        </div>
      </form>
    </>
  );
}
